import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTimes, FaSave, FaUser, FaMapMarkerAlt, FaSpinner } from 'react-icons/fa';

const TraderModal = ({ isOpen, onClose, trader, userInfo, API_BASE_URL, auth, onSave, onNotification }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    phone: '',
    countryCode: '+233',
    business_name: '',
    national_info: '',
    id_type: '',
    group_id: '',
    status: 'active'
  });
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Location tracking state
  const [locationData, setLocationData] = useState({
    latitude: null,
    longitude: null,
    address: '',
    accuracy: null
  });
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');

  // Function to get current location
  const getCurrentLocation = () => {
    setLocationLoading(true);
    setLocationError('');

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      setLocationLoading(false);
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000 // Cache for 1 minute
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;

        setLocationData({
          latitude,
          longitude,
          accuracy,
          address: ''
        });

        // Try to get address using reverse geocoding (optional)
        try {
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=v1.public.eyJqdGkiOiI4MjM1YTE4NS00ZjBkLTQzNWItOTBjYy0wZDlmNGZhNjE0ZTcifTAFLwwypCRs1_BqhMeu4hkGTxcy3H2Pqp_TZbbplWeojkUGqHFfXzpQrqbmgTzfT-N7cRYEhBVNrw87qZkp3jS1MoyU5T1OlptmoI3ZJDK9dZt3cS1ixmzx_0_8HuorvlEezzpSURu_1HjUM_9DGr8LfxXPgIZCHsyuKHOZbDkCfqTk2_g9JAiYEnrfIMgQ8DaXAUjE1Vb_pK29eyzzzNIuDsqbCgeO9ui6Gj4XtJLrUQ8vsIagG_qwA4JBrGV_uhx6_7zrk1cbLC7o13pueIvgyURNxCksP8m3-efbINX4B-J187w3FC9lFQeEHDXm8skDsifxdFvGLyhZqYNA_HQ.ZWU0ZWIzMTktMWRhNi00Mzg0LTllMzYtNzlmMDU3MjRmYTkx`
          );

          if (response.ok) {
            const data = await response.json();
            if (data.features && data.features.length > 0) {
              const address = data.features[0].place_name;
              setLocationData(prev => ({
                ...prev,
                address
              }));
            }
          }
        } catch (error) {
          console.log('Could not get address:', error);
          // Address is optional, so we don't show error to user
        }

        setLocationLoading(false);
      },
      (error) => {
        let errorMessage = 'Unable to get location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
          default:
            errorMessage = 'An unknown error occurred';
            break;
        }
        setLocationError(errorMessage);
        setLocationLoading(false);
      },
      options
    );
  };

  useEffect(() => {
    if (isOpen) {
      fetchGroups();
      if (trader) {
        // Edit mode - handle both individual name fields and owner_name
        let first_name = trader.first_name || '';
        let middle_name = trader.middle_name || '';
        let last_name = trader.last_name || '';

        // If individual name fields are empty but owner_name exists, split it
        if (!first_name && !last_name && trader.owner_name) {
          const nameParts = trader.owner_name.trim().split(' ');
          first_name = nameParts[0] || '';
          if (nameParts.length === 2) {
            last_name = nameParts[1] || '';
          } else if (nameParts.length > 2) {
            middle_name = nameParts.slice(1, -1).join(' ');
            last_name = nameParts[nameParts.length - 1] || '';
          }
        }

        // Parse phone number to separate country code and phone
        let phoneNumber = trader.phone || '';
        let countryCode = '+233';

        if (phoneNumber.startsWith('+233')) {
          countryCode = '+233';
          phoneNumber = phoneNumber.substring(4); // Remove +233
        } else if (phoneNumber.startsWith('+')) {
          // Handle other country codes
          const match = phoneNumber.match(/^(\+\d{1,4})(.*)/);
          if (match) {
            countryCode = match[1];
            phoneNumber = match[2];
          }
        }

        setFormData({
          first_name,
          middle_name,
          last_name,
          phone: phoneNumber,
          countryCode: countryCode,
          business_name: trader.business_name || '',
          national_info: trader.national_info || '',
          id_type: trader.id_type || '',
          group_id: trader.group_id || '',
          status: trader.status || 'active'
        });
      } else {
        // Add mode - reset form
        setFormData({
          first_name: '',
          middle_name: '',
          last_name: '',
          phone: '',
          countryCode: '+233',
          business_name: '',
          national_info: '',
          id_type: '',
          group_id: '',
          status: 'active'
        });
      }
      setErrors({});
    }
  }, [isOpen, trader]);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/zonal-groups?zone_id=${userInfo.zone_id}`,
        auth
      );
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };



  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9+\-\s()]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Validate zone assignment
    if (!userInfo.zone_id) {
      alert('You must be assigned to a zone to manage traders. Please contact your administrator.');
      return;
    }

    setLoading(true);
    try {
      // Combine name fields into owner_name for backend compatibility
      const owner_name = [
        formData.first_name?.trim(),
        formData.middle_name?.trim(),
        formData.last_name?.trim()
      ].filter(Boolean).join(' ');

      // Combine country code with phone number for database storage
      const fullPhoneNumber = formData.phone ? `${formData.countryCode}${formData.phone}` : '';

      const traderData = {
        ...formData,
        phone: fullPhoneNumber, // Store the full phone number with country code
        owner_name, // Backend expects this field
        mmda_id: userInfo.mmda_id,
        zone_id: userInfo.zone_id,
        created_by: userInfo.user_id,
        group_id: formData.group_id || null, // Handle empty group selection
        // Include location data if available
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        location_address: locationData.address,
        location_accuracy: locationData.accuracy
      };

      // Remove countryCode from the data sent to backend as it's now included in phone
      delete traderData.countryCode;

      // Debug: Log the data being sent
 

      let response;

      if (trader) {
        // Update existing trader
        response = await axios.put(
          `${API_BASE_URL}/traders/${trader.trader_id}`,
          traderData,
          auth
        );

 

        // Show success notification for update

        if (onNotification) {
          onNotification({
            show: true,
            type: 'success',
            title: 'Trader Updated Successfully',
            message: response.data.message || `${owner_name || 'Trader'} has been updated successfully.`
          });
        }

      } else {
        // Create new trader
        response = await axios.post(
          `${API_BASE_URL}/traders`,
          traderData,
          auth
        );

        console.log('Create response:', response.data);

        // Show success notification for create
        console.log('Setting create notification...');
        if (onNotification) {
          onNotification({
            show: true,
            type: 'success',
            title: 'Trader Added Successfully',
            message: response.data.message || `${owner_name || 'New trader'} has been added successfully.`
          });
        }
        console.log('Create notification set');
      }

      // Call onSave after a delay to ensure notification is shown
      setTimeout(() => {
        onSave();
      }, 100);
      // Don't close immediately, let user see the success message
    } catch (error) {
      console.error('Error saving trader:', error);
      console.error('Error response:', error.response?.data);

      // Show error notification
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          'Failed to save trader. Please try again.';

      if (onNotification) {
        onNotification({
          show: true,
          type: 'error',
          title: trader ? 'Update Failed' : 'Add Trader Failed',
          message: errorMessage
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Main Modal */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        padding: '2rem',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <h2 style={{ margin: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FaUser color="#3498db" />
            {trader ? 'Edit Trader' : 'Add New Trader'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#7f8c8d'
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {/* First Name */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                style={{
                  width: '95%',
                  padding: '0.75rem',
                  border: `1px solid ${errors.first_name ? '#e74c3c' : '#ddd'}`,
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
                placeholder="Enter first name"
              />
              {errors.first_name && (
                <span style={{ color: '#e74c3c', fontSize: '0.8rem' }}>{errors.first_name}</span>
              )}
            </div>

            {/* Middle Name */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Middle Name
              </label>
              <input
                type="text"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleInputChange}
                style={{
                  width: '95%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
                placeholder="Enter middle name"
              />
            </div>

            {/* Last Name */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                style={{
                  width: '95%',
                  padding: '0.75rem',
                  border: `1px solid ${errors.last_name ? '#e74c3c' : '#ddd'}`,
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
                placeholder="Enter last name"
              />
              {errors.last_name && (
                <span style={{ color: '#e74c3c', fontSize: '0.8rem' }}>{errors.last_name}</span>
              )}
            </div>

            {/* Phone */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Phone Number *
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select
                  name="countryCode"
                  value={formData.countryCode || '+233'}
                  onChange={handleInputChange}
                  style={{
                    width: '80px',
                    padding: '0.75rem 0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.25rem',
                    fontSize: '1rem',
                    outline: 'none',
                    backgroundColor: 'white',
                  }}
                >
                  <option value="+233">🇬🇭 +233</option>
                  <option value="+234">🇳🇬 +234</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+27">🇿🇦 +27</option>
                </select>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: `1px solid ${errors.phone ? '#e74c3c' : '#ddd'}`,
                    borderRadius: '0.25rem',
                    fontSize: '1rem',
                    outline: 'none',
                  }}
                  placeholder="241234567"
                />
              </div>
              {errors.phone && (
                <span style={{ color: '#e74c3c', fontSize: '0.8rem' }}>{errors.phone}</span>
              )}
            </div>

            {/* Business Name */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Business Name
              </label>
              <input
                type="text"
                name="business_name"
                value={formData.business_name}
                onChange={handleInputChange}
                style={{
                  width: '95%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
                placeholder="Enter business name"
              />
            </div>

            {/* Group */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Trader Group
              </label>
              <select
                name="group_id"
                value={formData.group_id}
                onChange={handleInputChange}
                style={{
                  width: '95%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                <option value="">Select a group (optional)</option>
                {groups.map(group => (
                  <option key={group.group_id} value={group.group_id}>
                    {group.group_name}
                  </option>
                ))}
              </select>
            </div>

            {/* National ID */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                National ID
              </label>
              <input
                type="text"
                name="national_info"
                value={formData.national_info}
                onChange={handleInputChange}
                style={{
                  width: '95%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
                placeholder="Enter national ID"
              />
            </div>

            {/* Location Section */}
            <div style={{
              gridColumn: '1 / -1',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '0.5rem',
              border: '1px solid #e9ecef'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1rem'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: '500',
                  fontSize: '1rem'
                }}>
                  <FaMapMarkerAlt style={{ color: '#e74c3c' }} />
                  Trader Location
                </label>
                <button
                  type="button"
                  onClick={getCurrentLocation}
                  disabled={locationLoading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: locationLoading ? '#95a5a6' : '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: locationLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  {locationLoading ? (
                    <>
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                      Getting Location...
                    </>
                  ) : (
                    <>
                      <FaMapMarkerAlt />
                      Get Current Location
                    </>
                  )}
                </button>
              </div>

              {locationError && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: '0.25rem',
                  color: '#c33',
                  fontSize: '0.9rem',
                  marginBottom: '1rem'
                }}>
                  {locationError}
                </div>
              )}

              {locationData.latitude && locationData.longitude && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: '#efe',
                  border: '1px solid #cfc',
                  borderRadius: '0.25rem',
                  fontSize: '0.9rem'
                }}>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>📍 Location Captured:</strong>
                  </div>
                  <div style={{ marginBottom: '0.25rem' }}>
                    <strong>Coordinates:</strong> {locationData.latitude.toFixed(6)}, {locationData.longitude.toFixed(6)}
                  </div>
                  {locationData.accuracy && (
                    <div style={{ marginBottom: '0.25rem' }}>
                      <strong>Accuracy:</strong> ±{Math.round(locationData.accuracy)}m
                    </div>
                  )}
                  {locationData.address && (
                    <div>
                      <strong>Address:</strong> {locationData.address}
                    </div>
                  )}
                </div>
              )}

              <div style={{
                fontSize: '0.8rem',
                color: '#666',
                marginTop: '0.5rem',
                fontStyle: 'italic'
              }}>
                💡 Capturing the trader's location helps with mapping and route planning for future visits.
              </div>
            </div>

            {/* Status */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                style={{
                  width: '95%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: loading ? '#95a5a6' : '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <FaSave />
              {loading ? 'Saving...' : 'Save Trader'}
            </button>
          </div>
        </form>
      </div>
    </div>
      )}


    </>
  );
};

export default TraderModal;
